import React, { useState, useEffect } from 'react';
import { useFeatureLimits } from './FeatureLimitsProvider';
import { Modal, Alert, Button, Spinner } from 'react-bootstrap';
import { FaExclamationTriangle, FaCrown, FaLock } from 'react-icons/fa';
import SideNav from '../helpers/SideNavbar';
import { useNavigate, Link } from 'react-router-dom';

const FeatureAccessGuard = ({ 
  children, 
  featureName, 
  onAccessDenied = null,
  showUpgradeButton = true,
  customMessage = null 
}) => {
  const navigate = useNavigate();
  const {
    checkFeatureAccess,
    logFeatureUsage,
    isFreeUser,
    isSubscriber,
    getFeatureLimit,
    hasReachedLimit,
    getRemainingLimit
  } = useFeatureLimits();

  const [accessStatus, setAccessStatus] = useState({
    loading: true,
    canProceed: false,
    remainingLimit: 0,
    resetDate: null,
    error: null
  });

  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    checkAccess();
  }, [featureName]);

  const checkAccess = async () => {
    try {
      const response = await checkFeatureAccess(featureName);
      setAccessStatus({
        loading: false,
        canProceed: response.canProceed,
        remainingLimit: response.remainingLimit,
        resetDate: response.resetDate,
        error: response.error
      });

      if (!response.canProceed && onAccessDenied) {
        onAccessDenied(response);
      }
    } catch (error) {
      setAccessStatus({
        loading: false,
        canProceed: false,
        remainingLimit: 0,
        resetDate: null,
        error: error.message
      });
    }
  };

  const handleFeatureUsage = async (metadata = null) => {
    if (accessStatus.canProceed) {
      await logFeatureUsage(featureName, 1, metadata);
      // Re-check access after logging usage
      await checkAccess();
    }
  };

  const getFeatureDisplayName = () => {
    const names = {
      'knack_moments': 'KnackMoments',
      'knack_feeds': 'KnackFeeds',
      'manual_posts': 'Create Posts',
      'polls': 'Polls',
      'schedule_days': 'Schedule Posts',
      'challenges': 'Knack Challenges'
    };
    return names[featureName] || featureName;
  };

  const getLimitMessage = () => {
    if (customMessage) return customMessage;

    const limit = getFeatureLimit(featureName);
    
    if (limit && limit.limit_value === 0) {
      return `${getFeatureDisplayName()} is not available for free users. Please upgrade to access this feature.`;
    }

    if (hasReachedLimit(featureName)) {
      const resetDate = accessStatus.resetDate ? new Date(accessStatus.resetDate).toLocaleDateString() : 'soon';
      return `You've reached your limit for ${getFeatureDisplayName()}. Your limit will reset ${resetDate}.`;
    }

    return `You don't have access to ${getFeatureDisplayName()}.`;
  };

  const renderAccessDenied = () => {
    const limit = getFeatureLimit(featureName);
    const isBlocked = limit && limit.limit_value === 0;

    return (
      <SideNav>
        <div className="card shadow-sm p-3 mt-3" style={{ border: 'none', backgroundColor: '#fff' }}>
          <div className="card-header" style={{ border: 'none', backgroundColor: '#fff' }}>
            <Link to="/post">
              <h5 className="welcome-text" style={{ fontSize: '20px', color: '#2e3192', textAlign: 'left' }}>
                <i className="fa fa-arrow-left" style={{marginRight:'30px'}} aria-hidden="true"></i>
                {getFeatureDisplayName()}
              </h5>
            </Link>
            <hr />
          </div>
          <div className="card-body text-center p-4">
            <div className="mb-3">
              {isBlocked ? (
                <FaLock className="text-danger" style={{ fontSize: '3rem' }} />
              ) : (
                <FaExclamationTriangle className="text-warning" style={{ fontSize: '3rem' }} />
              )}
            </div>
            
            <h5 className="mb-3">
              {isBlocked ? 'Feature Not Available' : 'Limit Reached'}
            </h5>
            
            <p className="text-muted mb-4">
              {getLimitMessage()}
            </p>

            {isBlocked && (featureName === 'knack_moments' || featureName === 'challenges') && (
              <div className="row mb-4">
                <div className="col-12 col-md-8 offset-md-2">
                  <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                    <iframe
                      width="100%"
                      height="100%"
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                      src={featureName === 'knack_moments' 
                        ? 'https://www.youtube.com/embed/kFilvlitfYc'
                        : 'https://www.youtube.com/embed/fjSupgaMYyE'}
                      title={`${getFeatureDisplayName()} Tutorial Video`}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>
              </div>
            )}

            <div className="mb-3">
              <Button 
                variant="primary" 
                size="lg"
                onClick={() => navigate('/feature-limits')}
                className="me-2"
              >
                View Limits
              </Button>
              
              {showUpgradeButton && (
                <Button 
                  variant="success" 
                  size="lg"
                  onClick={() => navigate('/subscription')}
                  className="me-2"
                >
                  <FaCrown className="me-2" />
                  Upgrade
                </Button>
              )}
            </div>

            {!isBlocked && accessStatus.remainingLimit > 0 && (
              <div className="alert alert-info">
                <strong>Remaining:</strong> {accessStatus.remainingLimit} uses available
              </div>
            )}
          </div>
        </div>
      </SideNav>
    );
  };

  if (accessStatus.loading) {
    return (
      <SideNav>
        <div className="card shadow-sm p-3 mt-3" style={{ border: 'none', backgroundColor: '#fff' }}>
          <div className="card-header" style={{ border: 'none', backgroundColor: '#fff' }}>
            <Link to="/post">
              <h5 className="welcome-text" style={{ fontSize: '20px', color: '#2e3192', textAlign: 'left' }}>
                <i className="fa fa-arrow-left" style={{marginRight:'30px'}} aria-hidden="true"></i>
                {getFeatureDisplayName()}
              </h5>
            </Link>
            <hr style={{ margin: '10px 0', borderColor: '#dee2e6' }} />
          </div>
          <div className="card-body text-center p-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Checking access...</span>
            </Spinner>
            <p className="mt-2">Checking feature access...</p>
          </div>
        </div>
      </SideNav>
    );
  }

  if (accessStatus.error) {
    return (
      <SideNav>
        <div className="card shadow-sm p-3 mt-3" style={{ border: 'none', backgroundColor: '#fff' }}>
          <div className="card-header" style={{ border: 'none', backgroundColor: '#fff' }}>
            <Link to="/post">
              <h5 className="welcome-text" style={{ fontSize: '20px', color: '#2e3192', textAlign: 'left' }}>
                <i className="fa fa-arrow-left" style={{marginRight:'30px'}} aria-hidden="true"></i>
                {getFeatureDisplayName()}
              </h5>
            </Link>
            <hr style={{ margin: '10px 0', borderColor: '#dee2e6' }} />
          </div>
          <div className="card-body">
            <Alert variant="danger">
              <Alert.Heading>Error Checking Access</Alert.Heading>
              <p>{accessStatus.error}</p>
              <Button variant="outline-danger" onClick={checkAccess}>
                Retry
              </Button>
            </Alert>
          </div>
        </div>
      </SideNav>
    );
  }

  if (!accessStatus.canProceed) {
    return renderAccessDenied();
  }

  // If access is granted, render children with usage logging capability
  return (
    <div>
      {React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, {
            onFeatureUsage: handleFeatureUsage,
            remainingLimit: accessStatus.remainingLimit
          });
        }
        return child;
      })}
    </div>
  );
};

export default FeatureAccessGuard;
